import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Package, ShoppingCart, Users, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    products: 0,
    orders: 0,
    users: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [productsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/products?per_page=1`),
          axios.get(`${API}/orders?per_page=1`)
        ]);

        let usersCount = 0;
        if (user?.role === 'admin' || user?.role === 'sales') {
          const usersRes = await axios.get(`${API}/users?per_page=1`);
          usersCount = usersRes.data?.total || 0;
        }

        setStats({
          products: productsRes.data?.total || 0,
          orders: ordersRes.data?.total || 0,
          users: usersCount
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    
    fetchStats();
  }, [user?.role]);

  const statCards = [
    { name: 'Total Products', value: stats.products, icon: Package, color: 'bg-indigo-50 text-indigo-600', link: '/products' },
    { name: 'My Orders', value: stats.orders, icon: ShoppingCart, color: 'bg-emerald-50 text-emerald-600', link: '/orders' },
  ];

  if (user?.role === 'admin' || user?.role === 'sales') {
    statCards.push({ name: 'Total Users', value: stats.users, icon: Users, color: 'bg-amber-50 text-amber-600', link: '/users' });
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="dashboard-title">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="mt-1 sm:mt-2 text-sm sm:text-base lg:text-lg text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
            Here&apos;s what&apos;s happening with your business today.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="stats-grid">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link
                key={stat.name}
                to={stat.link}
                data-testid={`stat-card-${stat.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-4 sm:p-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-slate-600 uppercase tracking-wide">{stat.name}</p>
                    <p className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-semibold text-slate-900">{stat.value}</p>
                  </div>
                  <div className={`p-2.5 sm:p-3 rounded-lg ${stat.color}`}>
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6 lg:p-8">
          <div className="text-center">
            <TrendingUp className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-1 sm:mb-2">Quick Actions</h3>
            <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6">Get started with common tasks</p>
            
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
              {(user?.role === 'admin' || user?.role === 'sales') && (
                <Link
                  to="/products/new"
                  data-testid="quick-action-create-product"
                  className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-medium transition-all active:scale-[0.98] text-sm sm:text-base"
                >
                  Create Product
                </Link>
              )}
              <Link
                to="/orders/new"
                data-testid="quick-action-create-order"
                className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium transition-all active:scale-[0.98] text-sm sm:text-base"
              >
                Create Order
              </Link>
              <Link
                to="/orders"
                data-testid="quick-action-view-orders"
                className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-white border border-slate-200 text-slate-900 rounded-md hover:bg-slate-50 font-medium transition-all text-sm sm:text-base"
              >
                View All Orders
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
